import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Comportamiento ÚNICO de las ventanas emergentes (guardar / cerrar).
 *
 *  · Guardar: pide «¿Está seguro de que desea guardar los cambios?». Si se cancela, vuelve al formulario sin
 *    perder nada. Si se confirma, ejecuta `onSave` (valida y guarda; cierra la ventana SOLO si el guardado fue
 *    exitoso y, si no, muestra el error y deja la ventana abierta con los datos). Mientras guarda, cualquier clic
 *    o Enter adicional se ignora: no se duplican registros.
 *  · Cerrar (X, Cancelar, tecla Escape o clic en el fondo): si hay cambios sin guardar, pide confirmación antes
 *    de descartarlos. El fondo solo cierra si el clic EMPEZÓ y TERMINÓ en el fondo (seleccionar texto en un
 *    campo y soltar el mouse afuera ya no cierra la ventana).
 *
 * Uso:
 *   const m = useModalForm({ value: form, onClose, onSave: save, abierto: open });
 *   <div {...m.overlayProps} className="fixed inset-0 …">
 *     <div> … <button onClick={m.cerrar}>X</button> <button onClick={m.cerrar}>Cancelar</button>
 *       <button onClick={m.guardar} disabled={m.saving}>Guardar</button>      (o <form onSubmit={m.guardar}>)
 *   `value` = objeto del formulario (para detectar cambios); sin `value` la ventana cierra sin preguntar.
 *   `abierto` = solo para ventanas que se muestran/ocultan dentro de una página (por defecto true).
 *   `listo`   = false mientras la ventana carga sus datos iniciales (no cuentan como cambios del usuario).
 *               Puede ser un número de versión: cada vez que cambia (p. ej. al recargar después de guardar en una
 *               ventana que sigue abierta), los datos de ese momento pasan a ser la referencia.
 */
export const MSG_GUARDAR = "¿Está seguro de que desea guardar los cambios?";
export const MSG_DESCARTAR = "Hay cambios sin guardar. ¿Desea descartarlos y cerrar la ventana?";

// Ventanas abiertas (la última es la de arriba): Escape solo cierra la ventana superior
const pila = [];
let secuencia = 0;

const snap = (v) => {
  try { return JSON.stringify(v ?? null); } catch { return ""; }
};

export function useModalForm({ value, onClose, onSave, confirmar = true, mensaje = MSG_GUARDAR, abierto = true, listo = true } = {}) {
  const inicial = useRef(snap(value));
  const lock = useRef(false);
  const abajoEnFondo = useRef(false);
  const [saving, setSaving] = useState(false);
  const cerrarRef = useRef(null);

  // Al abrir (o volver a abrir) la ventana —y cuando terminan de cargar sus datos (`listo`)— los datos de ese
  // momento son la referencia de «sin cambios»
  const tocado = useRef(false);
  useEffect(() => {
    if (abierto) tocado.current = false;
    if (abierto && listo) inicial.current = snap(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, listo]);
  // Mientras el usuario no haya tocado la ventana, lo que la propia ventana carga o rellena por defecto
  // (bodega principal, cuenta sugerida, datos del servidor…) también es parte de la referencia
  useEffect(() => {
    if (abierto && listo && !tocado.current) inicial.current = snap(value);
  });

  // Valores actuales en refs: «¿hay cambios?» se evalúa en el momento de cerrar (no con un valor de un render
  // anterior; al abrir una ventana dentro de una página, la referencia se fija después del primer render).
  const valueRef = useRef(value);
  valueRef.current = value;
  const estadoRef = useRef({ abierto, listo });
  estadoRef.current = { abierto, listo };
  const hayCambios = () => {
    const { abierto: ab, listo: li } = estadoRef.current;
    return Boolean(ab && li) && valueRef.current !== undefined && snap(valueRef.current) !== inicial.current;
  };
  const dirty = Boolean(abierto && listo) && value !== undefined && snap(value) !== inicial.current;

  const cerrar = useCallback(() => {
    if (lock.current) return;                                    // no se cierra a mitad de un guardado
    if (hayCambios() && !window.confirm(MSG_DESCARTAR)) return;
    if (onClose) onClose();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);
  cerrarRef.current = cerrar;

  const guardar = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (lock.current) return undefined;                          // doble clic / Enter repetido: se ignora
    if (confirmar && !window.confirm(mensaje)) return undefined; // cancelar: vuelve al formulario intacto
    lock.current = true;
    setSaving(true);
    try {
      return await onSave(e);
    } finally {
      lock.current = false;
      setSaving(false);
    }
  }, [confirmar, mensaje, onSave]);

  useEffect(() => {
    if (!abierto || !onClose) return undefined;
    const id = ++secuencia;
    pila.push(id);
    const h = (ev) => {
      if (ev.key === "Escape" && pila[pila.length - 1] === id && cerrarRef.current) {
        ev.stopImmediatePropagation();
        cerrarRef.current();
      }
    };
    window.addEventListener("keydown", h);
    return () => {
      window.removeEventListener("keydown", h);
      const i = pila.indexOf(id);
      if (i >= 0) pila.splice(i, 1);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const marcarTocado = () => { tocado.current = true; };
  const overlayProps = {
    onKeyDownCapture: marcarTocado, onInputCapture: marcarTocado, onChangeCapture: marcarTocado, onPointerDownCapture: marcarTocado,
    onMouseDown: (ev) => { abajoEnFondo.current = ev.target === ev.currentTarget; },
    onClick: (ev) => {
      if (ev.target === ev.currentTarget && abajoEnFondo.current) cerrar();
      abajoEnFondo.current = false;
    },
  };
  return { guardar, cerrar, saving, dirty, overlayProps };
}

/** Acciones puntuales dentro de una ventana (aprobar, anular, importar…): una sola ejecución a la vez y,
 *  si se indica, confirmación previa. */
export function useAccionUnica() {
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (fn, mensaje) => {
    if (lock.current) return undefined;
    if (mensaje && !window.confirm(mensaje)) return undefined;
    lock.current = true; setBusy(true);
    try { return await fn(); } finally { lock.current = false; setBusy(false); }
  }, []);
  return { run, busy };
}
